import { FetchError } from 'ofetch'
import type { MiddlewareCallbackParams } from 'openapi-fetch'
import type { Merge } from 'type-fest'

/**
 * Thrown when a fetch error occurs in the client created from useApi.
 *
 * To check if an unknown error is an instance of an ApiError, use the standard ApiError.isError(error) static method,
 * which also acts as a type-guard.
 *
 * @example
 * ```ts
 * onError(error) {
 *   if (ApiError.isError(error)) {
 *     // Error is now correctly typed as an ApiError
 *      console.log("Error is:",
 *          error.data,
 *          error.request,
 *          error.response,
 *          error.status
 *      )
 *
 *   }
 * }
 * ```
 */
export class ApiError<T = any> extends FetchError<T> {
    static override isError(error: unknown): error is ApiError<any> {
        return error instanceof ApiError || error instanceof FetchError
    }
}

/**
 * True when the error carries a 5xx HTTP status — a server-side failure rather than a
 * user-provoked one. Used so an "expected" error classification can never mask a real
 * server error.
 */
export function isServerError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false
    const { statusCode, status, response } = error as { statusCode?: number, status?: number, response?: { status?: number } }
    const code = statusCode ?? status ?? response?.status
    return typeof code === 'number' && code >= 500
}

export interface OpenapiContext<T> extends Merge<
    MiddlewareCallbackParams,
    {
        response?: Response & {
            _data: T
        }
        error?: unknown
    }> {}

/**
 * Thrown when a fetch error occurs in the client created from useOpenapi.
 *
 * To check if an unknown error is an instance of an OpenapiError, use the standard OpenapiError.isError(error) static method,
 * which also acts as a type-guard.
 *
 * @example
 * ```ts
 * onError(error) {
 *   if (OpenapiError.isError(error)) {
 *     // Error is now correctly typed as an OpenapiError
 *      console.log("Error is:",
 *          error.data,
 *          error.request,
 *          error.response,
 *          error.status
 *      )
 *
 *   }
 * }
 * ```
 */
export class OpenapiError<T = any> extends FetchError<T> {
    private context: OpenapiContext<T>
    override cause?: {
        message: string
        request: {
            url: MiddlewareCallbackParams['request']['url']
            schemaPath: MiddlewareCallbackParams['schemaPath']
            params: MiddlewareCallbackParams['params']
        }
        response?: Response & {
            _data: T
        }
    }

    constructor(
        context: OpenapiContext<T>,
    ) {
        let message = `[${context.request.method}] Request to ${context.request.url} failed with`

        if (context.response) {
            message += ` status code: ${context.response.status}`
        }
        else {
            if (context.error instanceof Error && context.error?.message) {
                message += ` ${context.error.message}`
            }
            else {
                message += '<no response>'
            }
        }

        super(message, {
            cause: {
                message: (context.response?._data as any)?.message || message,
                request: {
                    url: context.request.url,
                    schemaPath: context.schemaPath,
                    params: context.params,
                },
                response: context.response?.clone()
            }
        })

        this.name = 'OpenapiError'
        this.context = context
    }

    override get data() {
        return this.context.response?._data
    }

    override get status() {
        return this.context.response?.status
    }

    override get statusCode() {
        return this.context.response?.status
    }

    override get statusText() {
        return this.context.response?.statusText
    }

    override get statusMessage() {
        return this.context.response?.statusText
    }

    static override isError(error: unknown): error is OpenapiError<any> {
        return error instanceof OpenapiError
    }
}
